import { useMemo, useState } from 'react';
import { Button, Select } from '@ff-it/ui';
import { useField } from 'react-final-form';
import equal from 'fast-deep-equal';
import { ExportBuilderDialog, reconcilePresetColumns, validateColumns } from 'components/ExportBuilder';
import type { ExportColumn, ExportFieldGroup } from 'components/ExportBuilder';
import type { ExportPreset } from '../columns';
import { useExportPresets } from './useExportPresets';

type FieldProps = { groups: ExportFieldGroup[]; exportKey: string };
type ResolvedPreset = ExportPreset & { resolvedColumns: ExportColumn[]; message: string | undefined };

export function ExportColumnsField({ groups, exportKey }: FieldProps): JSX.Element {
  const { presets, error, ...actions } = useExportPresets(exportKey);
  if (!presets) {
    return (
      <p role={error ? 'alert' : 'status'}>{error ? 'Unable to load export presets.' : 'Loading export presets…'}</p>
    );
  }
  return <PresetField groups={groups} presets={presets} {...actions} />;
}

type PresetFieldProps = {
  groups: ExportFieldGroup[];
  presets: ExportPreset[];
} & Omit<ReturnType<typeof useExportPresets>, 'presets' | 'error'>;

function PresetField({ groups, presets, save, remove, busy }: PresetFieldProps): JSX.Element {
  const resolvedPresets = useMemo<ResolvedPreset[]>(() => {
    const legacy = presets.find((preset) => preset.id === null);
    return presets.map((preset) => {
      const { columns, skipped } = reconcilePresetColumns(groups, preset.columns, legacy?.columns ?? []);
      return {
        ...preset,
        resolvedColumns: columns,
        message: skipped
          ? `${skipped} unavailable column(s) were skipped.`
          : !equal(columns, preset.columns)
            ? 'Required columns were restored.'
            : undefined,
      };
    });
  }, [groups, presets]);
  const legacy = resolvedPresets.find((preset) => preset.id === null);
  const preferred = resolvedPresets.find((preset) => preset.is_default);
  const [initial] = useState(() => preferred?.resolvedColumns ?? []);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(preferred?.id);
  const { input, meta } = useField<ExportColumn[]>('columns', {
    // Initialize only an empty form; retained report columns always win.
    defaultValue: initial,
    validate: (columns) => validateColumns(groups, columns),
    subscription: { value: true, error: true },
  });
  const current = input.value;
  const [message, setMessage] = useState(() => (equal(current, initial) ? preferred?.message : undefined));
  const matches = resolvedPresets.filter((preset) => equal(current, preset.resolvedColumns));
  const active = matches.find((preset) => preset.id === selected) ?? matches[0];
  const savedId = active?.id;
  const savedDefault = preferred?.id != null ? preferred : undefined;
  const defaultTarget = active?.id === null ? savedDefault : active;

  function selectPreset(preset: ResolvedPreset): void {
    if (
      !active &&
      !equal(current, preset.resolvedColumns) &&
      !window.confirm('Discard custom columns and load another preset?')
    )
      return;
    input.onChange(preset.resolvedColumns);
    setSelected(preset.id);
    setMessage(preset.message);
  }

  function applyPreset(preset?: ExportPreset): void {
    input.onChange(preset?.columns ?? []);
    setSelected(preset?.id);
  }

  function applyColumns(columns: ExportColumn[], is_default: boolean) {
    if (defaultTarget && is_default !== active?.is_default) {
      return save({ is_default: savedId != null && is_default }, () => input.onChange(columns), defaultTarget.id);
    }
    input.onChange(columns);
  }

  return (
    <div className="mb-4">
      <div className="d-flex align-items-end flex-wrap mb-2">
        <div className="mr-2" style={{ width: '280px', maxWidth: '100%' }}>
          <label htmlFor="raw-export-preset" className="d-block mb-1">
            Preset
          </label>
          <Select<ResolvedPreset>
            inputId="raw-export-preset"
            aria-label="Preset"
            size="sm"
            isSearchable
            isClearable={false}
            options={resolvedPresets}
            getOptionValue={(preset) => String(preset.id)}
            getOptionLabel={(preset) =>
              `${preset.name}${preset.id === null ? ' (built-in)' : preset.is_default ? ' (default)' : ''}`
            }
            noOptionsMessage={() => 'No matching presets'}
            value={active ?? null}
            placeholder="Custom"
            onChange={(option) => {
              if (option) selectPreset(option);
            }}
            disabled={busy}
          />
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          icon="table-columns"
          onClick={() => {
            setMessage(undefined);
            setOpen(true);
          }}
        >
          Configure {current.length} columns
        </Button>
      </div>
      {message && !open && (
        <p role="status" className="text-muted">
          {message}
        </p>
      )}
      <ExportBuilderDialog
        open={open}
        groups={groups}
        columns={current}
        onApply={applyColumns}
        onSaveAs={(columns, name, is_default) => save({ name, columns, is_default }, applyPreset)}
        onUpdate={
          savedId != null ? (columns, is_default) => save({ columns, is_default }, applyPreset, savedId) : undefined
        }
        preset={{
          name: active?.name ?? 'Custom',
          columns: active?.columns,
          caption: active ? (active.id === null ? 'Built-in preset' : 'Saved preset') : 'Current columns',
          isDefault: active?.is_default ?? false,
          canChangeDefault: !!defaultTarget,
          onDelete: savedId != null ? () => remove(savedId, () => applyPreset(legacy)) : undefined,
        }}
        busy={busy}
        onClose={() => setOpen(false)}
      />
      {meta.error && (
        <p role="alert" className="text-danger mt-2 mb-0">
          {meta.error}
        </p>
      )}
    </div>
  );
}
